import PropTypes from 'prop-types';
import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Link from 'next/link';
import { getFBTProductsBySearch } from 'aster-foundation/core/epharmacy/Product/redux/action';
import { dataLayerSendEvent, getProductItemObject } from 'Epharmacy/EpharmacyAnalytics';
import { MY_ASTER_CATEGORY } from '@common/Constants/analytics';
import callNewRecommendationApi from 'Epharmacy/CommonCarouselCall';
import logger from '@common/Utility/logger';
import { AE_GLOBAL, LOCALSTORAGE_KEYS } from '@common/Utility/constants';
import { debounce } from 'lodash';
import SlickSlider from '../SlickSlider/SlickSlider';
import ProductItem from '../ProductItem/ProductItem';
import SectionBox from '../SectionBox/SectionBox';
import { getViewAllLink, getAttributeItemFromArray } from '@common/Utility/common-utils';
import CommonProductCarousel from '../CommonProductCarousel/CommonProductCarousel';
import withAnalytics from '../Hoc/WithAnalytics';
import { useNavigationURL } from '@common/Utility/generateNavigationURL';

const InsiderRecCarousel = ({ insiderRecommendation, variationId, asterRecommendation, productId, catId, insiderData, linkWebViewAll = '', analyticsProps, setBuyAgainData,setCartFiller_insider,setShowBrandCarousel,setShowCategoryCarousel }) => {
  const dispatch = useDispatch();
  const zoneData = useSelector((state) => state?.app?.userLocationReducer?.zone);
  const userLogin = useSelector((state) => state?.astercore?.checkUserExist?.data?.entry?.[0]?.resource);
  const guestLogin = useSelector((state) => state?.astercore?.guestAccessToken?.data);
  const [products, setProducts] = useState([]);
  const [recommendedSku, setRecommendedSku] = useState([]);
  const [carouselData, setCarouselData] = useState({});
  const carouselViewRef = useRef();
  const carouselViewEventFired = useRef();
  const generateURL = useNavigationURL();

  const responsive = [
    {
      breakpoint: 980,
      settings: {
        slidesToShow: 5,
      },
    },
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 3,
      },
    },
    {
      breakpoint: 480,
      settings: {
        slidesToShow: 1,
        variableWidth: true,
        infinite: true,
      },
    },
  ];

  const onSuccess = (response, item) => {
    if (response?.title === AE_GLOBAL?.BUY_AGAIN) {
      setBuyAgainData(response);
    }
    setCarouselData({ ...carouselData, ...response });
  };
  const onFailure = (err, item) => {
    if (err && item?.position === 1) {
      setCarouselData({});
    }
  };

  useEffect(() => {
    if(!insiderData) return;
    const userZone = zoneData?.join(',') || localStorage?.getItem(AE_GLOBAL.USER_ZONE);
    if(insiderData?.campaignId === variationId) {
      const recommendedSkuIds = insiderData?.products?.map(item => item?.item_id);
      setRecommendedSku(recommendedSkuIds);
      if(recommendedSkuIds?.length) {
        dispatch(
          getFBTProductsBySearch({
            params: { skuIds: recommendedSkuIds, zone: userZone },
            onSuccess: (res) => {
              if (insiderRecommendation?.title === AE_GLOBAL?.CART_FILLERS) {
                setCartFiller_insider(res);
              }
              if (insiderRecommendation?.title !== AE_GLOBAL?.CART_FILLERS) {
                setProducts(res);
              }
            },
            onFailure: (err) => {
              logger.error('insider error', err);
            },
          }),
        );
      }
    }
  }, [insiderData, zoneData]);

  const rightTopSection = () => {
    if (linkWebViewAll) {
        return (
          <Link href={generateURL(linkWebViewAll)} passHref>
            <a className="link me-2" tabIndex="0" role="button" aria-pressed="false" href={generateURL(linkWebViewAll)}>
              View All
            </a>
          </Link>
        );
    }
    return '';
  };

  const callRecommendation = debounce(() => {
    callNewRecommendationApi(
      dispatch,
      { zoneData, userLogin, guestLogin },
      {
        offerType: asterRecommendation?.offerType,
        type: asterRecommendation?.type,
        asterRecommendation,
        productIds: productId,
        categoryId: catId,
        onSuccess,
        onFailure,
      },
    );
  }, 500);

  useEffect(() => {
    callRecommendation();
  }, [zoneData, userLogin, guestLogin]);

  useEffect(() => {
    if((!products || products?.length === 0 || !insiderRecommendation?.active) && (!carouselData ||
      carouselData?.data?.length === 0 || !asterRecommendation?.active)){
      setShowBrandCarousel(true);
      setShowCategoryCarousel(true);
    }
  }, [products,carouselData]);

  const handleInsiderCarouselView = () => {
    if(insiderRecommendation?.active && products?.length > 0 && !carouselViewEventFired.current){
      carouselViewEventFired.current = true;
      dataLayerSendEvent('view_item_list', {
        ecommerce: {
          items: products?.map((element, index) => ({ ...getProductItemObject(element), index })),
        },
        section_name: insiderRecommendation?.title,
        screen_name: analyticsProps?.screenName || 'pharmacy_Home_Page',
        page_type: analyticsProps?.pageType || 'Epharmacy Homepage',
        page_id: analyticsProps?.pageId,
        location: analyticsProps?.location,
        referrer_page: analyticsProps?.referrerPage,
        myaster_category: MY_ASTER_CATEGORY.AE,
      });
    }
  };

  const InsiderCarouselWithAnalytics = withAnalytics(
    () => (
      <div ref={carouselViewRef} className={`ins-preview-wrapper-${variationId}`}>
        <div className="ins-web-smart-recommender-body" data-recommended-items={JSON.stringify(recommendedSku)}>
          <div className="ins-web-smart-recommender-box-item">
            <SectionBox
              title={insiderRecommendation?.title}
              customClass="ePharmacy"
              customOuterClass="sectionBox whiteBg"
              rightTop={rightTopSection()}
            >
              <SlickSlider
                sliderSettings={{
                  responsive,
                  slidesToShow: 5,
                  infinite: false,
                  className: '',
                  dots: false,
                }}
              >
                {products &&
                  products?.map((item, index) => {
                    const smallImage = item.small_image;
                    return (
                      <div
                      className="ins-product-box ins-element-link ins-add-to-cart-wrapper ins-sr-api"
                        ins-product-id={item?.sku}
                        event-collection="true"
                      >
                        <ProductItem
                          key={item?.productId}
                          index={index}
                          avgRating={item?.avgRating}
                          badgeDetails={item?.badgeDetails}
                          offerBadge={item?.badgeDetails?.offerBadge}
                          asterBadge={item?.badgeDetails?.asterBadge?.[0]}
                          categoryImage={smallImage || getAttributeItemFromArray(item?.customAttribute, 'small_image')}
                          name={item?.name}
                          newPrice={item?.specialPrice || item?.special_price}
                          actualPrice={item?.price}
                          express={item?.express || item?.express_bool}
                          isFlash={item?.isFlash}
                          deliverBy={item?.deliverBy}
                          isOptics={item?.isOpticalProduct}
                          deliveryText={item?.deliveryText}
                          currency={item?.currency}
                          freeDelivery={item?.freeDelivery}
                          outOfStock={!item?.inStock}
                          sku={item?.sku}
                          availableStock={item?.quantity}
                          isScheduled={item?.isScheduled}
                          product={item}
                          orderWithin={item?.orderWithin}
                          sourceContainerTitle='Recommended For You'
                          isLoader={false}
                          pageName={analyticsProps?.screenName || 'pharmacy_Home_Page'}
                          sectionName={insiderRecommendation?.title}
                          itemListName='Recommended For You'
                          itemListId={item?.offerType}
                          screen="Epharmacy Homepage"
                          pageType={analyticsProps?.pageType || 'Epharmacy Homepage'}
                          hasRX={item?.itemLabels?.includes(AE_GLOBAL?.RX)}
                        />
                      </div>
                    );
                  })}
              </SlickSlider>
            </SectionBox>
          </div>
        </div>
      </div>
    ),
    handleInsiderCarouselView,
    carouselViewRef,
  );

  return (
    <>
      {insiderRecommendation?.active && products?.length > 0 ? (
        <InsiderCarouselWithAnalytics/>
      ) : (
        asterRecommendation?.active && carouselData &&
        carouselData?.data?.length > 0 && (
          <CommonProductCarousel
            key={asterRecommendation?.offerType}
            brandId={asterRecommendation?.offerType}
            data={{ productList: carouselData }}
            title={asterRecommendation?.title}
            showViewAllLink={carouselData?.showViewAll}
            viewAllLink={getViewAllLink(asterRecommendation)}
            pageName={analyticsProps?.screenName || 'pharmacy_Home_Page'}
            sectionTitle={asterRecommendation?.title}
            screenType={analyticsProps?.screenName}
            pageType={analyticsProps?.pageType}
            referrerPage={analyticsProps?.referrerPage}
            bgColor={asterRecommendation?.bgColor}
          />
        )
      )}
    </>
  );
};

InsiderRecCarousel.propTypes = {
  analyticsProps: PropTypes.object,
  setShowBrandCarousel: PropTypes.func,
  setShowCategoryCarousel: PropTypes.func,
};

InsiderRecCarousel.defaultProps = {
  analyticsProps: {},
  setShowBrandCarousel: () => {},
  setShowCategoryCarousel: () => {},
};

export default InsiderRecCarousel;
